import { httpClient } from './http';

export type Table = {
  id: string;
  number: number;
  capacity: number;
  location: string;
  restaurantId: string;
  restaurantName?: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  waiters?: any[]; // <-- Agregado para evitar error de linter
};

export interface CreateTableData {
  number: number;
  capacity: number;
  location: 'interior' | 'exterior' | 'barra' | 'vip' | 'terraza';
}

export interface UpdateTableData {
  number?: number;
  capacity?: number;
  location?: 'interior' | 'exterior' | 'barra' | 'vip' | 'terraza';
  status?: 'active' | 'inactive' | 'maintenance';
}

export interface TableWaiterAssignment {
  table_id: string;
  waiter_id: string;
  restaurant_id: string;
  is_occupied?: boolean;
}

export const tablesService = {
  /**
   * Obtiene todas las mesas de un restaurante
   */
  async getTables(userId: string, restaurantId: string) {
    try {
      console.log('🏪 getTables - Llamando endpoint con:', { userId, restaurantId });
      const response = await httpClient.get(
        `/system/users/${userId}/restaurants/${restaurantId}/tables`,
        {},
        'authenticated'
      );
      console.log('✅ getTables - Respuesta del backend:', response);
      return response;
    } catch (error) {
      console.error('❌ getTables - Error:', error);
      throw error;
    }
  },

  /**
   * Crea una nueva mesa
   */
  async createTable(userId: string, restaurantId: string, tableData: CreateTableData) {
    try {
      console.log('🏪 createTable - Llamando endpoint con:', { userId, restaurantId, tableData });
      const response = await httpClient.post(
        `/system/users/${userId}/restaurants/${restaurantId}/tables`,
        tableData,
        'authenticated'
      );
      console.log('✅ createTable - Mesa creada:', response);
      return response;
    } catch (error) {
      console.error('❌ createTable - Error:', error);
      throw error;
    }
  },

  /**
   * Actualiza una mesa existente
   */
  async updateTable(userId: string, restaurantId: string, tableId: string, tableData: UpdateTableData) {
    try {
      console.log('🏪 updateTable - Llamando endpoint con:', { userId, restaurantId, tableId, tableData });
      const response = await httpClient.put(
        `/system/users/${userId}/restaurants/${restaurantId}/tables/${tableId}`,
        tableData,
        'authenticated'
      );
      console.log('✅ updateTable - Mesa actualizada:', response);
      return response;
    } catch (error) {
      console.error('❌ updateTable - Error:', error);
      throw error;
    }
  },

  async getTableTokenByTableId(userId: string, restaurantId: string, tableId: string) {
    try {
      const response = await httpClient.get(
        `/system/users/${userId}/restaurants/${restaurantId}/tables/${tableId}/token`,
        {},
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('❌ getTableTokenByTableId - Error:', error);
      throw error;
    }
  },

  /**
   * Elimina una mesa
   */
  async deleteTable(userId: string, restaurantId: string, tableId: string) {
    try {
      console.log('🏪 deleteTable - Llamando endpoint con:', { userId, restaurantId, tableId });
      const response = await httpClient.delete(
        `/system/users/${userId}/restaurants/${restaurantId}/tables/${tableId}`,
        'authenticated'
      );
      console.log('✅ deleteTable - Mesa eliminada');
      return response;
    } catch (error) {
      console.error('❌ deleteTable - Error:', error);
      throw error;
    }
  },

  /**
   * Obtiene una mesa específica
   */
  async getTable(userId: string, restaurantId: string, tableId: string) {
    try {
      console.log('🏪 getTable - Llamando endpoint con:', { userId, restaurantId, tableId });
      const response = await httpClient.get(
        `/system/users/${userId}/restaurants/${restaurantId}/tables/${tableId}`,
        {},
        'authenticated'
      );
      console.log('✅ getTable - Mesa obtenida:', response);
      return response;
    } catch (error) {
      console.error('❌ getTable - Error:', error);
      throw error;
    }
  },

  /**
   * Cambia el estado de una mesa
   */
  async changeTableStatus(userId: string, restaurantId: string, tableId: string, status: string) {
    try {
      console.log('🏪 changeTableStatus - Llamando endpoint con:', { userId, restaurantId, tableId, status });
      const response = await httpClient.patch(
        `/system/users/${userId}/restaurants/${restaurantId}/tables/${tableId}/status`,
        { status },
        'authenticated'
      );
      console.log('✅ changeTableStatus - Estado cambiado:', response);
      return response;
    } catch (error) {
      console.error('❌ changeTableStatus - Error:', error);
      throw error;
    }
  },

  /**
   * Asigna mesas del día a meseros
   */
  async assignTablesToWaiters(
    userId: string, 
    restaurantId: string, 
    assignments: TableWaiterAssignment[]
  ) {
    try {
      console.log('🏪 assignTablesToWaiters - Llamando endpoint con:', { userId, restaurantId, assignments });
      
      // Usar el endpoint real de asignación de mesas a meseros (igual que React Native)
      const promises = assignments.map(assignment => 
        httpClient.post(
          `/system/users/${userId}/restaurants/${restaurantId}/waiters/assign-table`,
          assignment,
          'authenticated'
        )
      );
      
      const results = await Promise.all(promises);
      console.log('✅ assignTablesToWaiters - Asignaciones completadas:', results);
      return results.map((result: any) => result?.data || result);
    } catch (error) {
      console.error('❌ assignTablesToWaiters - Error:', error);
      throw error;
    }
  },

  /**
   * Obtiene asignaciones de mesas del día
   */
  async getTableAssignments(
    userId: string, 
    restaurantId: string, 
    date?: string
  ) {
    try {
      console.log('🏪 getTableAssignments - Llamando endpoint con:', { userId, restaurantId, date });
      const params = date ? `?date=${date}` : '';
      const response = await httpClient.get(
        `/system/users/${userId}/restaurants/${restaurantId}/tables/assignments${params}`,
        {},
        'authenticated'
      );
      console.log('✅ getTableAssignments - Asignaciones obtenidas:', response);
      return response;
    } catch (error) {
      console.error('❌ getTableAssignments - Error:', error);
      throw error;
    }
  }
}; 